import {
  RegisterUserPayload,
  RegisterUserResponse,
  VerifyOtpResponse,
  GetAllUsersResponse,
  GetAllActivityLogsResponse,
  UserDetail,
} from '@/types/user';

export interface RoleProfile {
  name: string;
}

export const sendOtp = async (phoneNumber: string) => {
  const response = await fetch('/api/v1/auth/send-otp', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phoneno: phoneNumber }),
  });
  const result = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(result?.message || result?.errors?.[0]?.message || 'Failed to send OTP');
  }
  return result?.data ?? result;
};

export const verifyOtp = async (phoneNumber: string, code: string) => {
  const response = await fetch('/api/v1/auth/verify-otp', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phoneno: phoneNumber, code }),
  });
  const result: VerifyOtpResponse | null = await response.json().catch(() => null);
  if (!response.ok) {
    const r = result as unknown as { message?: string; errors?: Array<{ message: string }> } | null;
    throw new Error(r?.message || r?.errors?.[0]?.message || 'Failed to verify OTP');
  }
  return result?.data ?? result;
};

export const registerUser = async (userData: RegisterUserPayload) => {
  const response = await fetch('/api/v1/auth/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(userData),
  });
  const result = await response.json().catch(() => null) as RegisterUserResponse | null;
  if (!response.ok) {
    const msg =
      (result as { message?: string } | null)?.message ||
      (result as { errors?: Array<{ message: string }> } | null)?.errors?.[0]?.message ||
      'Failed to register user';
    throw new Error(msg);
  }
  return result?.data ?? result;
};

export const getAllUsers = async (): Promise<UserDetail[]> => {
  const response = await fetch('/api/v1/users?limit=200', {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) throw new Error('Failed to get all users');
  const result = await response.json();
  const items = result?.data?.items;
  return Array.isArray(items) ? items : [];
};

export const getUserByEmail = async (email: string): Promise<UserDetail | null> => {
  const filters = encodeURIComponent(JSON.stringify([['email', '=', email]]));
  const response = await fetch(
    `/api/v1/users?filters=${filters}&limit=1`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );
  if (!response.ok) throw new Error('Failed to get user by email');
  const result = await response.json();
  const items = result?.data?.items;
  return Array.isArray(items) && items.length > 0 ? items[0] : null;
};

export const getUserById = async (userId: string): Promise<UserDetail> => {
  const response = await fetch(
    `/api/v1/users/detail?name=${encodeURIComponent(userId)}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }
  );
  if (!response.ok) throw new Error('Failed to get user by id');
  const result = await response.json();
  return result?.data?.item ?? result?.data ?? result;
};

export const createUser = async (userData: any): Promise<any> => {
  const response = await fetch('/api/v1/users', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(userData),
  });
  if (!response.ok) throw new Error('Failed to create user');
  const result = await response.json();
  return result?.data?.item ?? result?.data ?? result;
};

export const updateUser = async (userId: string, userData: any): Promise<any> => {
  const response = await fetch('/api/v1/users/update', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: userId, ...userData }),
  });
  if (!response.ok) throw new Error('Failed to update user');
  const result = await response.json();
  return result?.data?.item ?? result?.data ?? result;
};

export const deleteUser = async (userId: string): Promise<any> => {
  const response = await fetch('/api/v1/users/delete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: userId }),
  });
  if (!response.ok) throw new Error('Failed to delete user');
  const result = await response.json();
  return result?.data ?? result;
};

export const getAllActivityLogs = async (): Promise<any[]> => {
  const response = await fetch('/api/v1/users/activity-logs?limit=200', {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) throw new Error('Failed to get all activity logs');
  const result = await response.json();
  const items = result?.data?.items;
  return Array.isArray(items) ? items : [];
};
